import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, MessageCircle, ExternalLink, ShieldCheck, Globe as GlobeIcon, Instagram, Facebook } from "lucide-react";
import api from "@/lib/api";
import { socialLink } from "@/lib/socialLink";
import BusinessStatus, { OpeningHoursTable } from "@/components/BusinessStatus";

// Inline TikTok icon — Lucide doesn't ship one yet
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.53 1h3.3v14.16a3.42 3.42 0 1 1-3.42-3.42h.36v3.3h-.36a.12.12 0 0 0 0 .24.13.13 0 0 0 .12-.12V1zm4.99 4.13a5.6 5.6 0 0 0 4.48 2.2v3.3a8.9 8.9 0 0 1-4.48-1.2v6.03a6.72 6.72 0 1 1-6.72-6.72c.28 0 .55.02.83.05v3.3a3.42 3.42 0 1 0 2.59 3.32V1h3.3v4.13z"/>
  </svg>
);

const DEFAULT_SOCIALS = [
  { key: "whatsapp1", label: "WhatsApp Business",   value: "+673 722 2952", href: "https://wa.me/6737222952",             icon: MessageCircle, color: "text-emerald-700 hover:bg-emerald-50 border-emerald-200" },
  { key: "whatsapp2", label: "WhatsApp Alternative", value: "+673 744 3080", href: "https://wa.me/6737443080",             icon: MessageCircle, color: "text-emerald-700 hover:bg-emerald-50 border-emerald-200" },
  { key: "instagram", label: "Instagram",            value: "@adelina.agency", href: "https://www.instagram.com/adelina.agency", icon: Instagram,     color: "text-pink-700 hover:bg-pink-50 border-pink-200" },
  { key: "facebook",  label: "Facebook",             value: "Adelina Employment Agency", href: "https://www.facebook.com/share/1CHgCEU1az", icon: Facebook, color: "text-blue-800 hover:bg-blue-50 border-blue-200" },
  { key: "tiktok",    label: "TikTok",               value: "@adelina.agency", href: "https://www.tiktok.com/@adelina.agency", icon: TikTokIcon, color: "text-zinc-950 hover:bg-zinc-100 border-zinc-300" },
  { key: "linktree",  label: "Linktree · All Links", value: "linktr.ee/adelinaemploymentagency", href: "https://linktr.ee/adelinaemploymentagency", icon: ExternalLink, color: "text-lime-800 hover:bg-lime-50 border-lime-200" },
  { key: "website",   label: "Alt. Website",         value: "adelinaagency.com", href: "http://www.adelinaagency.com/", icon: GlobeIcon, color: "text-amber-800 hover:bg-amber-50 border-amber-200" },
];

const DEFAULT_OFFICE_ADDRESS = "1st Floor, Unit A6, The Curve, Batu, Block A, Jalan Batu Besurat, Bandar Seri Begawan, Brunei";
const DEFAULT_MAPS_LINK = "https://maps.app.goo.gl/LTMGURxgj8Sh8cYF8";

/**
 * Build the socials list, preferring values in admin CMS (`settings.social`)
 * over hardcoded defaults. Every visible link is still editable via Settings.
 */
function buildSocials(social) {
  const s = social || {};
  const map = [
    { key: "whatsapp1", label: "WhatsApp Business", href: s.whatsapp || s.whatsapp_business || "https://wa.me/6737222952", value: s.whatsapp_number || "+673 722 2952", icon: MessageCircle, color: "text-emerald-700 hover:bg-emerald-50 border-emerald-200" },
    { key: "instagram", label: "Instagram", href: s.instagram || "https://www.instagram.com/adelina.agency", value: (s.instagram || "").replace(/https?:\/\/(www\.)?instagram\.com\//, "@") || "@adelina.agency", icon: Instagram, color: "text-pink-700 hover:bg-pink-50 border-pink-200" },
    { key: "facebook",  label: "Facebook",  href: s.facebook || "https://www.facebook.com/share/1CHgCEU1az", value: "Adelina Employment Agency", icon: Facebook, color: "text-blue-800 hover:bg-blue-50 border-blue-200" },
    { key: "tiktok",    label: "TikTok",    href: s.tiktok || "https://www.tiktok.com/@adelina.agency", value: (s.tiktok || "").replace(/https?:\/\/(www\.)?tiktok\.com\//, "") || "@adelina.agency", icon: TikTokIcon, color: "text-zinc-950 hover:bg-zinc-100 border-zinc-300" },
    { key: "linktree",  label: "Linktree · All Links", href: s.linktree || "https://linktr.ee/adelinaemploymentagency", value: (s.linktree || "").replace(/https?:\/\/(www\.)?/, "") || "linktr.ee/adelinaemploymentagency", icon: ExternalLink, color: "text-lime-800 hover:bg-lime-50 border-lime-200" },
    ...(s.website ? [{ key: "website", label: "Website", href: s.website, value: s.website.replace(/https?:\/\/(www\.)?/, ""), icon: GlobeIcon, color: "text-amber-800 hover:bg-amber-50 border-amber-200" }] : []),
  ];
  // If admin CMS is empty (no social object at all), fall back to full default list
  if (!social || Object.keys(social).length === 0) return DEFAULT_SOCIALS;
  return map.map(x => ({ ...x, href: socialLink(x.key, x.href) })).filter(x => x.href);
}

export default function About() {
  const { t } = useTranslation();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const OFFICE_ADDRESS = settings?.address || DEFAULT_OFFICE_ADDRESS;
  const MAPS_LINK = settings?.google_maps_link || DEFAULT_MAPS_LINK;
  const MAPS_EMBED = settings?.google_maps_embed || "";
  const SOCIALS = buildSocials(settings?.social);
  const milestones = [
    { y: "2013", t: t("about.year_2013"), d: t("about.year_2013_d") },
    { y: "2017", t: t("about.year_2017"), d: t("about.year_2017_d") },
    { y: "2021", t: t("about.year_2021"), d: t("about.year_2021_d") },
    { y: "2025", t: t("about.year_2025"), d: t("about.year_2025_d") },
  ];
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="overline mb-4">{t("about.overline", "About Us")}</div>
      <h1 className="font-serif text-5xl md:text-6xl text-zinc-950 tracking-tight max-w-4xl">
        {t("about.title", "Adelina Employment Agency")}
      </h1>

      {/* Government Licence Badge */}
      <div className="mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-zinc-950 text-white text-[12px] font-semibold" data-testid="licence-badge">
        <ShieldCheck size={14} className="text-[#d4a017]" />
        <span>🇧🇳 {t("about.licence", "Registered with Labor Dept · PJB/LAP/2025/140")}</span>
      </div>

      <p className="mt-8 text-lg text-zinc-950 leading-relaxed max-w-3xl">
        {settings?.about_text || t("about.default_desc", "Adelina Employment Agency supplies Amah & Foreign workers, officially licensed by the Ministry of Home Affairs, Brunei Darussalam. We connect trusted, vetted candidates with families & businesses across Brunei and the region.")}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
        <div className="bg-zinc-50 border border-zinc-200 rounded-3xl p-10">
          <div className="overline mb-3">{t("about.vision", "Vision")}</div>
          <p className="font-serif text-2xl leading-snug text-zinc-900">{settings?.vision}</p>
        </div>
        <div className="bg-zinc-950 text-white rounded-3xl p-10">
          <div className="overline text-[#d4a017] mb-3">{t("about.mission", "Mission")}</div>
          <p className="font-serif text-2xl leading-snug">{settings?.mission}</p>
        </div>
      </div>

      <div className="mt-16">
        <div className="overline mb-3">{t("about.values", "Our Values")}</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {(settings?.values || []).map((v) => (
            <div key={v} className="border border-zinc-200 rounded-2xl p-6 text-center">
              <div className="font-serif text-2xl text-zinc-950">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-20">
        <div className="overline mb-3">{t("about.timeline", "Company Timeline")}</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {milestones.map((m) => (
            <div key={m.y} className="border-t-2 border-[#b8860b] pt-4">
              <div className="text-[#b8860b] font-serif text-3xl">{m.y}</div>
              <div className="text-lg font-semibold mt-1">{m.t}</div>
              <p className="text-sm text-zinc-900 mt-2">{m.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* -------- Visit Us -------- */}
      <div className="mt-24 grid grid-cols-1 lg:grid-cols-5 gap-8 items-stretch">
        <div className="lg:col-span-3 bg-zinc-950 text-white rounded-3xl p-10 relative overflow-hidden">
          <div
            className="absolute -top-24 -right-24 w-[440px] h-[440px] rounded-full blur-3xl opacity-30 pointer-events-none"
            style={{ background: "radial-gradient(circle, #b8860b 0%, transparent 70%)" }}
          />
          <div className="relative">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="overline text-[#d4a017]">{t("about.visit_us", "Visit Our Office")}</div>
              <BusinessStatus size="md" variant="dark" showSub />
            </div>
            <div className="font-serif text-3xl md:text-4xl leading-tight tracking-tight mb-6">
              {t("about.visit_headline", "Stop by — we're happy to welcome you in person.")}
            </div>
            <div className="flex items-start gap-3 text-zinc-300 text-[15px] leading-relaxed">
              <MapPin size={18} className="text-[#d4a017] mt-0.5 shrink-0" />
              <span data-testid="office-address">{OFFICE_ADDRESS}</span>
            </div>
            <div className="mt-6">
              <OpeningHoursTable variant="dark" />
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={MAPS_LINK}
                target="_blank"
                rel="noreferrer"
                className="btn-gold inline-flex items-center gap-2 text-sm"
                data-testid="get-directions-btn"
              >
                <MapPin size={14} /> {t("about.get_directions", "Get directions")}
                <ExternalLink size={12} />
              </a>
              <a
                href={socialLink("whatsapp", settings?.social?.whatsapp || settings?.social?.whatsapp_business || settings?.whatsapp_number || "+6737222952")}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-700 hover:border-[#d4a017] text-sm transition-colors"
                data-testid="wa-primary-cta"
              >
                <MessageCircle size={14} /> {t("about.chat_now", "Chat on WhatsApp")}
              </a>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-3xl p-8">
          <div className="overline mb-4">{t("about.channels", "Reach Us")}</div>
          <div className="flex flex-col gap-2.5">
            {SOCIALS.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center gap-3 px-4 py-3 rounded-2xl border transition-colors ${s.color}`}
                  data-testid={`social-${s.key}`}
                >
                  <div className="w-9 h-9 rounded-full bg-white border border-current/20 flex items-center justify-center shrink-0">
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold opacity-70">{s.label}</div>
                    <div className="text-sm font-medium truncate">{s.value}</div>
                  </div>
                  <ExternalLink size={12} className="opacity-40 shrink-0" />
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* -------- Google Maps embed -------- */}
      {MAPS_EMBED && (
        <div className="mt-10 rounded-3xl overflow-hidden border border-zinc-200 shadow-sm" data-testid="google-maps-embed">
          <div className="flex items-center justify-between gap-3 px-5 py-3 bg-white border-b border-zinc-200 flex-wrap">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-[#b8860b]" />
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-600 font-semibold">
                {t("about.find_us_here", "Find Us Here")}
              </div>
            </div>
            <a
              href={MAPS_LINK}
              target="_blank"
              rel="noreferrer"
              className="text-[12px] text-[#b8860b] hover:text-[#8a5f07] font-semibold inline-flex items-center gap-1"
              data-testid="open-in-maps-link"
            >
              {t("about.open_in_maps", "Open in Google Maps")} <ExternalLink size={11} />
            </a>
          </div>
          <iframe
            title="Adelina Office Location"
            src={MAPS_EMBED}
            width="100%"
            height="420"
            style={{ border: 0, display: "block" }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}
    </div>
  );
}
